import { LODGE_EMAIL, LODGE_PHONE_SCHEMA } from "@/lib/contact-info";
import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead, DEFAULT_OG_IMAGE } from "@/lib/seo";
import { Star, Quote, PenLine } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

function reviewsJsonLd() {
  const avg =
    REVIEWS.reduce((s, r) => s + r.rating, 0) / Math.max(REVIEWS.length, 1);
  return {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "@id": "https://coffmancovelodging.com/#lodging",
    name: "Coffman Cove Lodging",
    url: "https://coffmancovelodging.com/reviews",
    image: DEFAULT_OG_IMAGE,
    telephone: LODGE_PHONE_SCHEMA,
    email: LODGE_EMAIL,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "306 Harbor Ave",
      addressLocality: "Coffman Cove",
      addressRegion: "AK",
      postalCode: "99918",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 56.0147,
      longitude: -132.8339,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg.toFixed(1),
      bestRating: "5",
      worstRating: "1",
      reviewCount: REVIEWS.length,
    },
    review: REVIEWS.map((r) => ({
      "@type": "Review",
      // itemReviewed is required by Google when Review is standalone. Nested
      // reviews inherit context from the parent LodgingBusiness, but we set
      // it explicitly so the Rich Results Test never flags it as missing.
      itemReviewed: {
        "@type": "LodgingBusiness",
        name: "Coffman Cove Lodging",
      },
      author: { "@type": "Person", name: r.name },
      datePublished: r.date,
      name: r.title ?? `${r.rating}-star review`,
      reviewBody: r.body,
      publisher: { "@type": "Organization", name: r.source },
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: "5",
        worstRating: "1",
      },
    })),
  };
}


export const Route = createFileRoute("/reviews")({
  head: () =>
    seoHead({
      path: "/reviews",
      title: "Guest Reviews — Coffman Cove Lodging",
      description:
        "Read what fishermen and families say about their stays at Coffman Cove Lodging on Prince of Wales Island, Alaska.",
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(reviewsJsonLd()),
        },
      ],
    }),
  component: ReviewsPage,
});


type Review = {
  name: string;
  location?: string;
  rating: number;
  date: string;
  source: string;
  title?: string;
  body: string;
};

const REVIEWS: Review[] = [
  {
    name: "BrookHaines",
    location: "Palmer, Alaska",
    rating: 5,
    date: "August 2019",
    source: "TripAdvisor",
    title: "Graduation Trip",
    body: "Our trip this year was another wonderful experience. I took my son along for a graduation trip before he starts college and we made some life long memories. I can't say enough about what a place Coffman Cove is for a self guided trip. They have definitely created the perfect getaway. Can't wait to plan the next visit.",
  },
  {
    name: "BrookHaines",
    location: "Palmer, Alaska",
    rating: 5,
    date: "March 2019",
    source: "TripAdvisor",
    title: "Perfect Alaskan Adventure",
    body: "I can't imagine a more perfect Alaskan fishing adventure. We've stayed at Coffman Cove several times and had an excellent time. Super excited for another trip next summer. Some spectacular scenery, wildlife, delicious food, perfect fishing setup and a most gracious host result in memories of a lifetime. For self guided fishing it doesn't get any better.",
  },
  {
    name: "RTArmstr",
    location: "Bloomsdale, Missouri",
    rating: 5,
    date: "July 2018",
    source: "TripAdvisor",
    title: "The best value for a self guided Alaska adventure PERIOD!",
    body: "This is my 4th trip to Coffman Cove. They provide everything you need to be successful. If you want to have an Alaska experience like no other, visit Coffman Cove. The food was exceptional, the lodging was comfortable, the boat meticulously maintained, and the gear of the highest quality. If you don't have your own terminal tackle, there's even a tackle shed that is well stocked and reasonably priced. Excellent communication before the trip and during your stay. They take perfect care of your catch. I have nothing but positive things to say.",
  },
  {
    name: "byuknis",
    location: "Wasilla, Alaska",
    rating: 5,
    date: "June 2017",
    source: "TripAdvisor",
    title: "Best Week Ever!!!",
    body: "Took the wife and two year old for a week of fishing at Coffman Cove. The owners were very accommodating. We got the use of a 20' Hewescraft for Ocean adventures and a vehicle to explore Prince of Wales Island. The locals are very friendly and helpful with suggestions on where and what to fish for. We are already planning a trip back soon. The wife got upset when I talked of doing a guys trip because she wants to go back as well!!!",
  },
  {
    name: "Mark T.",
    location: "Boise, ID",
    rating: 5,
    date: "August 2024",
    source: "TripAdvisor",
    title: "Best self-guided trip in SE Alaska",
    body: "Jerry and Travis Hedges have a great little spot where an average angler can come fish for a week, rent their own boat stocked with the bait and gear, and fish the inland passage without the expense of a charter. We limited out on Kings two days in a row.",
  },
  {
    name: "The Wilson Family",
    location: "Bend, OR",
    rating: 5,
    date: "July 2024",
    source: "Google",
    title: "Felt like family by day two",
    body: "Heather's breakfasts kept us full all morning out on the water. The boat was rigged and ready every day — downriggers set, bait in the cooler. Hailey even helped our kids with their first Halibut. We're already booked for next summer.",
  },
  {
    name: "Dan R.",
    location: "Austin, TX",
    rating: 5,
    date: "June 2024",
    source: "Facebook",
    body: "Forty years of doing this shows. The local intel on where the Kings were running saved us a full day of searching. Filleting, vacuum sealing, and freezing all handled — we flew home with a full fish box.",
  },
  {
    name: "Greg & Susan M.",
    location: "Minneapolis, MN",
    rating: 5,
    date: "September 2023",
    source: "TripAdvisor",
    title: "Quiet, beautiful, real Alaska",
    body: "We came for the fishing and stayed for the quiet. Eagles off the porch every morning, no crowds, and the kind of homestyle dinners you don't find at a fancy lodge. The Hedges family is the real deal.",
  },
  {
    name: "Chris B.",
    location: "Sacramento, CA",
    rating: 5,
    date: "July 2023",
    source: "Google",
    title: "Way better than a charter",
    body: "I've done the expensive charter thing in Sitka and Ketchikan. This was half the cost and twice the fun — you run your own day, fish where you want, and the gear is dialed. Coming back with my brother next year.",
  },
  {
    name: "Tony P.",
    location: "Spokane, WA",
    rating: 5,
    date: "August 2023",
    source: "TripAdvisor",
    body: "Clean rooms, great food, fast boats, and a dock 30 seconds from the door. Travis knows these waters cold — ask him where to go and listen. We caught Kings, Silvers, Halibut, and rockfish all in one week.",
  },
];

const STATS = [
  { v: "40+", l: "Years welcoming guests" },
  { v: "4.9", l: "Average rating" },
  { v: "85%", l: "Repeat & referred guests" },
];

function Stars({ n }: { n: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < n ? "fill-[var(--color-ember)] text-[var(--color-ember)]" : "text-muted-foreground/30"}
        />
      ))}
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function ReviewCard({ r }: { r: Review }) {
  return (
    <article className="relative rounded-lg border border-border bg-card p-6 flex flex-col h-full">
      {/* Header: avatar + name + date */}
      <header className="flex items-start gap-3">
        <div
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-deep)] text-white font-display text-sm"
        >
          {initials(r.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight truncate">{r.name}</p>
          {r.location && (
            <p className="text-xs text-muted-foreground truncate">{r.location}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">{r.date}</p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wider text-[var(--color-ember)] border border-[var(--color-ember)]/30 rounded-full px-2 py-0.5">
          {r.source}
        </span>
      </header>

      {/* Rating */}
      <div className="mt-4 flex items-center gap-2">
        <Stars n={r.rating} />
        <span className="text-xs text-muted-foreground">{r.rating.toFixed(1)} / 5</span>
      </div>

      {/* Body */}
      {r.title && (
        <h2 className="font-display text-lg mt-3 leading-snug">{r.title}</h2>
      )}
      <div className="relative mt-3 flex-1">
        <Quote
          size={20}
          aria-hidden
          className="absolute -top-1 -left-1 text-[var(--color-ember)]/25"
        />
        <p className="pl-6 text-sm leading-relaxed text-muted-foreground">
          {r.body}
        </p>
      </div>
    </article>
  );
}

function ReviewsPage() {
  const [guestReviews, setGuestReviews] = useState<(Review & { key: string })[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("guest_reviews")
        .select("id, author_name, author_location, rating, title, body, source, created_at")
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(60);
      if (cancelled || !data) return;
      setGuestReviews(
        data.map((r) => ({
          key: r.id,
          name: r.author_name,
          location: r.author_location ?? undefined,
          rating: r.rating,
          date: new Date(r.created_at).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          }),
          source: r.source,
          title: r.title ?? undefined,
          body: r.body,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--color-deep)] text-[var(--color-parchment)]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-16 sm:py-20 text-center">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">
            Guest Reviews
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 text-balance text-white">
            Forty years of fishermen, families, and friends.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/90 max-w-2xl mx-auto">
            What guests are saying about their week with Travis, Heather, and Hailey at
            Coffman Cove Lodging.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 md:gap-10 max-w-2xl mx-auto">
            {STATS.map((s) => (
              <div key={s.l}>
                <p className="font-display text-3xl md:text-4xl text-white">
                  {s.v}
                </p>
                <p className="mt-1 text-xs md:text-sm text-white/80">{s.l}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/submit-review"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
            >
              <PenLine size={16} />
              Write a review
            </Link>
          </div>
          <p className="mt-3 text-center text-xs text-white/70">
            Reviews are moderated before they appear on this page.
          </p>
        </div>
      </section>

      {/* Reviews grid */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
          {guestReviews.map((r) => (
            <ReviewCard key={r.key} r={r} />
          ))}
          {REVIEWS.map((r, i) => (
            <ReviewCard key={i} r={r} />
          ))}
        </div>
      </section>


      {/* CTA */}
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-balance text-white">
            Ready to write your own trip report?
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto">
            Weeks fill up fast for King and Halibut season. Lock in your dates with the Hedges
            family today.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="https://coffmancovelodging.com/contact"
              className="rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
            >
              Book Your Trip
            </a>
            <Link
              to="/contact"
              className="rounded-md border border-white/40 px-6 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white/10"
            >
              Ask a question
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
